import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  useAddWorkspaceMutation,
  useGetOrganizationsQuery,
  useGetAvailableWorkspacesQuery,
  useGetTenantsQuery,
} from '../../services/portal';

const CreateWorkspace = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors }
  } = useForm();

  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [availableWorkspaceCodes, setAvailableWorkspaceCodes] = useState([]);

  const { data: tenantData } = useGetTenantsQuery(); // assuming tenant list is fetched
  const { data: organizationsData } = useGetOrganizationsQuery({ forceRefetch: true });

  const { data: workspaceCodesData } = useGetAvailableWorkspacesQuery(
    selectedOrganization,
    {
      skip: !selectedOrganization,
      forceRefetch: true,
    }
  );

  const [addWorkspace, { isLoading: isSaving }] = useAddWorkspaceMutation();

  // Initialize tenant (non-editable) and organization (editable)
  useEffect(() => {
    if (tenantData?.tenants?.length > 0) {
      const firstTenant = tenantData.tenants[0];
      setSelectedTenant(firstTenant.tenant_id);
      setValue('tenant', firstTenant.tenant_id);
    }
  }, [tenantData]);

  useEffect(() => {
    if (organizationsData?.organizations?.length > 0) {
      const firstOrgId = organizationsData.organizations[0].organization_id;
      setSelectedOrganization(firstOrgId);
      setValue('organization', firstOrgId);
    }
  }, [organizationsData]);

  useEffect(() => {
    if (
      workspaceCodesData?.reportCodes?.length &&
      workspaceCodesData?.reportNames?.length &&
      workspaceCodesData.reportCodes.length === workspaceCodesData.reportNames.length
    ) {
      const codes = workspaceCodesData.reportCodes.map((code, index) => ({
        code,
        name: workspaceCodesData.reportNames[index],
      }));
      setAvailableWorkspaceCodes(codes);
    } else {
      setAvailableWorkspaceCodes([]);
    }
  }, [workspaceCodesData]);

  const onSubmit = (data) => {
    addWorkspace({
      workspace_name: data.name,
      workspace_shortname: 'XX',
      workspace_code: data.code,
      workspace_description: data.description,
      organization: {
        organization_id: selectedOrganization
      },
      tenant: {
        tenant_id: selectedTenant
      }
    })
      .unwrap()
      .then(() => navigate('/workspaces'))
      .catch((err) => console.error('Failed to create workspace:', err));
  };

  const handleCancel = () => navigate('/workspaces');

  const handleOrganizationChange = (e) => {
    const orgId = e.target.value;
    setSelectedOrganization(orgId);
    setValue('organization', orgId);
  };

  return (
    <div className="page">
      <div className="page-inner">
        <header className="page-title-bar">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item active">
                <a href="/workspaces">
                  <i className="breadcrumb-icon fa fa-angle-left mr-2"></i>
                  Workspaces
                </a>
              </li>
            </ol>
          </nav>
          <h1 className="page-title">New Workspace</h1>
        </header>

        <div className="page-section">
          <div className="card">
            <div className="card-body">
              <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                {/* Tenant (not editable) */}
                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Tenant</Form.Label>
                  <select
                    className="custom-select"
                    disabled
                    {...register('tenant')}
                    value={selectedTenant || ''}
                  >
                    {tenantData?.tenants?.map((tenant) => (
                      <option key={tenant.tenant_id} value={tenant.tenant_id}>
                        {tenant.tenant_name}
                      </option>
                    ))}
                  </select>
                </Form.Group>

                {/* Organization (editable) */}
                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Organization <span className="text-danger">*</span></Form.Label>
                  <select
                    className="custom-select"
                    {...register('organization', { required: true })}
                    value={selectedOrganization || ''}
                    onChange={handleOrganizationChange}
                  >
                    {organizationsData?.organizations?.map((org) => (
                      <option key={org.organization_id} value={org.organization_id}>
                        {org.organization_name}
                      </option>
                    ))}
                  </select>
                  {errors.organization && (
                    <p className="validate-feedback-invalid">
                      Organization is required.
                    </p>
                  )}
                </Form.Group>

                {/* Workspace Code (no placeholder) */}
                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Workspace Code <span className="text-danger">*</span></Form.Label>
                  <select
                    className="custom-select"
                    {...register('code', { required: true })}
                  >
                    {availableWorkspaceCodes.map((code) => (
                      <option key={code.code} value={code.code}>
                        {code.name}
                      </option>
                    ))}
                  </select>
                  {errors.code && (
                    <p className="validate-feedback-invalid">
                      Workspace code is required.
                    </p>
                  )}
                </Form.Group>

                {/* Workspace Name */}
                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Workspace Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Workspace name"
                    {...register('name', { required: true, minLength: 3 })}
                  />
                  {errors.name && (
                    <p className="validate-feedback-invalid">
                      Workspace name must be at least 3 characters.
                    </p>
                  )}
                </Form.Group>

                {/* Description */}
                <Form.Group className="form-group col-md-6 mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Description"
                    {...register('description')}
                  />
                </Form.Group>

                <hr className="mb-0 mt-0" />
                <div className="form-actions">
                  <Button variant="primary" type="submit" disabled={isSaving} className='btn2'>
                    Save
                  </Button>
                  <Button variant="secondary" style={{ marginLeft: 10 }} onClick={handleCancel} className='btn2'>
                    Cancel
                  </Button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkspace;
